import { SearchInterface } from "@amzn/ask-legal-domain";
import { Autosuggest, AutosuggestProps, InputProps } from "@amzn/awsui-components-react";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useLatestRequest } from "../../hooks/use-latest-request";
import { AppContext } from "../../setup/context";
import { callApi } from "../../utils/api-call";
import { getQueryParameterMap } from "../../utils/common-utils";

const SUGGESTION_DEBOUNCE_MS = 500;
const MIN_SUGGESTION_QUERY_LENGTH = 3;

export const SearchInputBarV2 = (props: {
    instanceId?: string;
    disabled?: boolean;
}) => {
    const navigate = useNavigate();
    const context = React.useContext(AppContext);

    const [queryText, setQueryText] = React.useState<string>("");
    const [querySuggestions, setQuerySuggestions] = React.useState<AutosuggestProps.Option[]>([]);
    const [statusType, setStatusType] = React.useState<AutosuggestProps.StatusType>("pending");
    // Kept in refs so they survive re-renders (a plain `let` is reset on every render).
    const suggestionTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();
    // Enter on a highlighted suggestion fires onSelect and then onKeyDown in the same event.
    const justSelectedRef = React.useRef(false);
    // Only the latest suggestion request may update the dropdown
    const suggestionRequest = useLatestRequest();
    const isGlobalSearch = !props.instanceId;

    const search = (query?: string) => {
        const searchQuery = query || queryText;
        if (searchQuery.length > 0) {
            if (isGlobalSearch) {
                // Global search route
                navigate(`/search?queryText=${encodeURIComponent(searchQuery)}`);
            } else {
                // Instance specific search route
                navigate(`/search/${props.instanceId}?queryText=${encodeURIComponent(searchQuery)}`);
            }
        }
    };

    const getOptionsFromSuggestions = (
        suggestions: SearchInterface.QuerySuggestionOutput | undefined
    ): AutosuggestProps.Option[] => {
        const options: AutosuggestProps.Option[] = [];
        if (!suggestions?.output?.Suggestions) return options;
        suggestions.output.Suggestions.forEach(suggestion => {
            if (suggestion.Value?.Text?.Text) {
                options.push({
                    value: suggestion.Value.Text.Text
                });
            }
        });
        return options;
    };

    const onKeyDownHandler = (event: { detail: InputProps.KeyDetail }) => {
        if (justSelectedRef.current) {
            justSelectedRef.current = false;
            return;
        }
        if (
            event.detail.key === "Enter" &&
            queryText.length > 0
        ) search();
    };

    const onSelectHandler = (event: { detail: AutosuggestProps.SelectDetail }) => {
        justSelectedRef.current = true;
        // A mouse selection has no following keydown, so clear the flag after this event.
        setTimeout(() => { justSelectedRef.current = false; });
        setQueryText(event.detail.value);
        search(event.detail.value);
    };

    const onChangeHandler = (event: { detail: InputProps.ChangeDetail }) => {
        setQueryText(event.detail.value);
    };

    const loadSuggestions = async (filteringText: string) => {
        const isCurrent = suggestionRequest.begin();
        // No loading state: suggestions are optional, so the dropdown only appears once they arrive
        const result = await callApi(
            context.getSearchAPI().querySuggestion,
            SearchInterface.QuerySuggestionInput.create(filteringText, props.instanceId!)
        );
        if (!isCurrent()) return;
        // Suggestions are optional, so a failed request just shows none
        setQuerySuggestions(result.ok ? getOptionsFromSuggestions(result.data) : []);
        setStatusType("finished");
    };

    const onLoadHandler = (event: { detail: AutosuggestProps.LoadItemsDetail }) => {
        // TODO - disable query suggestions for global search
        if (isGlobalSearch) {
            return;
        }
        clearTimeout(suggestionTimeoutRef.current);
        if (event.detail.filteringText.length < MIN_SUGGESTION_QUERY_LENGTH) {
            suggestionRequest.cancel();
            setQuerySuggestions([]);
            setStatusType("finished");
            return;
        }
        const filteringText = event.detail.filteringText;
        suggestionTimeoutRef.current = setTimeout(() => {
            loadSuggestions(filteringText);
        }, SUGGESTION_DEBOUNCE_MS);
    };

    React.useEffect(() => {
        return () => {
            clearTimeout(suggestionTimeoutRef.current);
        };
    }, []);

    React.useEffect(() => {
        // When component loads for search Results
        const windowHash = window.location.hash;
        if (windowHash.startsWith("#/search/")) {
            const queryParamMap = getQueryParameterMap();
            if (!!queryParamMap.get("queryText")) {
                setQueryText(queryParamMap.get("queryText"));
            }
        }
    }, []);

    const placeholder = isGlobalSearch
        ? "Global Search - Search across all team sites"
        : "Search PathFinder";

    return (
        <Autosuggest
            value={queryText}
            placeholder={placeholder}
            statusType={statusType}
            options={querySuggestions}
            filteringType="manual"
            i18nStrings={{ enteredTextLabel: value => `Search for "${value}"` }}
            onChange={onChangeHandler}
            onLoadItems={onLoadHandler}
            onKeyDown={onKeyDownHandler}
            onSelect={onSelectHandler}
            ariaLabel="Search PathFinder"
            ariaRequired
            disabled={props.disabled}
            expandToViewport={true}
        />);
};
